import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import _get from 'lodash/get';
import _isArray from 'lodash/isArray';

const LOCAL_STORE_PREFIX = process.env.VUE_APP_LOCAL_STORE_PREFIX;
const API_URL = process.env.VUE_APP_API_URL;
const CLAVEUNICA_LOGOUT_URL = process.env.VUE_APP_MINSAL_API_REFERENCE_URL + '/logout';

const minsalAxios = axios.create({
  baseURL: API_URL
});

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    drugForms: [],
    unitsOfTime: [],
    routeCodes: [],
    user: null,
    localToken: localStorage.getItem(`${LOCAL_STORE_PREFIX}-user-token`),
    activeOrganizationUuid: localStorage.getItem(`${LOCAL_STORE_PREFIX}-active-organization-uuid`)
  },
  getters: {
    user(state) {
      return state.user;
    },
    loggedIn(state) {
      return state.user !== null;
    },
    hasSavedSession(state) {
      return state.localToken !== null;
    },
    drugForms(state) {
      return state.drugForms;
    },
    unitsOfTime(state) {
      return state.unitsOfTime;
    },
    routeCodes(state) {
      return state.routeCodes;
    },
    activeOrganizationUuid(state) {
      return state.user ? state.activeOrganizationUuid : null;
    },
    activeOrganization(state) {
      return state.user.organizations.find(o => o.uuid === state.activeOrganizationUuid);
    }
  },
  actions: {
    async restoreUserSessionFromToken({ dispatch }, token) {
      // Set global Authorization header
      minsalAxios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      return dispatch('getUserInfo', token)
        .then(response => {
          return dispatch('restoreUserSession', { user: response.data, token });
        })
        .catch(e => {
          dispatch('logout', e);
        });
    },
    async logout({ commit, dispatch }) {
      minsalAxios.defaults.headers.common['Authorization'] = null;
      commit('LOGOUT');
      commit('CLEAR_ACTIVE_ORGANIZATION');
      dispatch('claveUnicaLogout');
    },
    async claveUnicaLogout() {
      window.location = `${CLAVEUNICA_LOGOUT_URL}?redirect=${encodeURIComponent(location.origin)}`;
    },
    async getUserInfo() {
      return minsalAxios.get(`/me`);
    },
    async previewMedicationRequest(context, id) {
      return minsalAxios.get('/medication-request/' + id + '/preview');
    },
    async changeFolioStatus(context, params) {
      // Todo: Convertir los ruts en IDS de minsal
      return minsalAxios.post('/update-printed-prescription', params)
        .then(response => {
          return Promise.resolve(response);
        })
        .catch(e => {
          return Promise.reject(e.response.data);
        });
    },
    async restoreUserSession({ commit }, { user, token }) {
      commit('LOGIN', { user, token });
      if (user.organizations.length === 1) {
        commit('SET_ACTIVE_ORGANIZATION', _get(user, 'organizations[0].uuid', null));
      }
      return Promise.resolve(user);
    },
    setActiveOrganizationUuid({ commit }, activeOrganizationUuid) {
      commit('SET_ACTIVE_ORGANIZATION', activeOrganizationUuid);
    },
    async fetchMedicationRequestDetails(context, medicationRequestId) {
      return minsalAxios.get(`/medication-request/${medicationRequestId}`)
        .then(response => {
          return Promise.resolve(response);
        })
        .catch(e => {
          const message = _get(e, 'response.status') === 404 ? 'Código de receta no encontrado' : 'No se ha podido establecer conexión con el servidor, intente más tarde';
          return Promise.reject(new Error(message));
        });
    },
    async searchMedication(context, search) {
      return minsalAxios.get(`/semantikos?search=${search}`);
    },
    async fetchMedicationDetails(context, medicationReference) {
      const medicationId = medicationReference.reference.split('/').pop();
      return minsalAxios.get(`/semantikos/${medicationId}`);
    },
    async fetchClinicalMedication({ commit }, medicationReference) {
      const medicationId = medicationReference.reference.split('/').pop();
      return minsalAxios.get(`/semantikos/${medicationId}/clinical-alternatives`);
    },
    async fetchRefsMedication({ commit }, medicationReference) {
      const medicationId = medicationReference.reference.split('/').pop();
      return minsalAxios.get(`/semantikos/${medicationId}/refs`);
    },
    async fetchCommercialAlternatives({ commit }, medicationReference) {
      const medicationId = medicationReference.reference.split('/').pop();
      return minsalAxios.get(`/semantikos/${medicationId}/commercial-alternatives`);
    },
    async fetchDispensableCommercialAlternatives(context, commercialMedicationReference) {
      const medicationId = commercialMedicationReference.reference.split('/').pop();
      return minsalAxios.get(`/semantikos/${medicationId}/dispensable-alternatives`);
    },
    async fetchMedicationDispenseMetadata({ dispatch }) {
      return Promise.all([
        dispatch('fetchMedicationDrugForms'),
        dispatch('fetchMedicationUnitsOfTime'),
        dispatch('fetchRouteCodes')
      ]);
    },
    async fetchMedicationDrugForms({ commit }) {
      return minsalAxios.get(`/fhir/drug-forms`)
        .then(response => {
          commit('FETCH_DRUG_FORMS_SUCCESS', response.data);
        });
    },
    async fetchMedicationUnitsOfTime({ commit }) {
      return minsalAxios.get(`/fhir/units-of-time`)
        .then(response => {
          commit('FETCH_UNITS_OF_TIME_SUCCESS', response.data);
        });
    },
    async fetchRouteCodes({ commit }) {
      return minsalAxios.get(`/fhir/route-codes`)
        .then(response => {
          commit('FETCH_ROUTE_CODES_SUCCESS', response.data);
        });
    },
    async saveMedicationDispense(context, { medicationDispense, groupId, location }) {
      const medicationDispenseData = { ...medicationDispense.data, groupId, location };
      return minsalAxios.post('medication-dispense', medicationDispenseData)
        .then(response => {
          return {
            medicationDispenseId: medicationDispense.id,
            medicationDispenseResponse: response.data
          };
        })
        .catch(e => {
          return Promise.reject({
            status: e.response.status,
            medicationDispenseId: medicationDispense.id,
            message: _get(e, 'response.data.error.message', 'Ha ocurrido un error al procesar el cambio de estado'),
            details: _get(e, 'response.data.error.details', []).reduce((details, fieldError) => {
              details[fieldError.field] = _isArray(fieldError.message) ? fieldError.message[0].message : fieldError.message;
              return details;
            }, {})
          });
        });
    },
    fetchMedicationDispenseGroup({ commit }, medicationDispenseGroupId) {
      return minsalAxios.get(`/medication-dispenses/dispense-groups/${medicationDispenseGroupId}`)
        .then(response => {
          return response.data;
        })
        .catch(e => {
          return e.response;
        });
    },
    async checkToken(context, payload) {
      return minsalAxios.get(`/check-token/${context.state.user.access_token}`);
    }
  },
  mutations: {
    LOGIN(state, { user, token }) {
      state.user = user;
      localStorage.setItem(`${LOCAL_STORE_PREFIX}-user-token`, token);
    },
    LOGOUT(state) {
      state.user = null;
      localStorage.removeItem(`${LOCAL_STORE_PREFIX}-user-token`);
    },
    SET_ACTIVE_ORGANIZATION(state, activeOrganizationUuid) {
      state.activeOrganizationUuid = activeOrganizationUuid;
      localStorage.setItem(`${LOCAL_STORE_PREFIX}-active-organization-uuid`, activeOrganizationUuid);
    },
    CLEAR_ACTIVE_ORGANIZATION(state) {
      state.activeOrganizationUuid = null;
      localStorage.removeItem(`${LOCAL_STORE_PREFIX}-active-organization-uuid`);
    },
    FETCH_DRUG_FORMS_SUCCESS(state, drugForms) {
      state.drugForms = drugForms;
    },
    FETCH_UNITS_OF_TIME_SUCCESS(state, unitsOfTime) {
      state.unitsOfTime = unitsOfTime;
    },
    FETCH_ROUTE_CODES_SUCCESS(state, routeCodes) {
      state.routeCodes = routeCodes;
    }
  }
});
